import { translate, translateRich, useLocale } from "../../localization";
import React, { useEffect, useState } from "react";
import { Alert, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { AppButton } from "../../components/ui";
import AccountsAPI from "../../services/AccountsAPI";
import { formatSubscriptionDate, getSubscriptionState } from "../../utility/subscriptionAccess";
import { BILLING_PLANS, PLAN_COMPARISON, formatPlanPrice, getAccountPlanName, matchesPurchasedPlan } from "../../utility/billingPlans";
import "./UpgradePage.scss";

const getApiResponseData = (response) => response?.data?.data ?? response?.data ?? null;

const UpgradePage = () => {
  useLocale();
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const [checkoutError, setCheckoutError] = useState("");
  const [redirectingPlan, setRedirectingPlan] = useState(null);
  const [activationState, setActivationState] = useState("idle");
  const [activatedSubscription, setActivatedSubscription] = useState(null);
  const [individualInterval, setIndividualInterval] = useState("year");
  const { refreshAuthSession, userId } = auth;
  const checkoutStatus = searchParams.get("checkout");
  const purchasedType = searchParams.get("purchaseType") || "classroom_license";
  const purchasedInterval = searchParams.get("billingInterval") || "year";
  const currentSubscription = getSubscriptionState({
    status: auth.subscriptionStatus,
    source: auth.subscriptionSource,
    expirationDate: auth.subscriptionEndsAt,
    renews: auth.subscriptionRenews,
    cancelAtPeriodEnd: auth.subscriptionCancelAtPeriodEnd,
  });
  const hasPaidPlan = currentSubscription.isSubscribed || Boolean(activatedSubscription);

  useEffect(() => {
    if (checkoutStatus !== "success" || !userId) return undefined;
    let cancelled = false;
    let timer;
    let attempt = 0;
    setActivationState("pending");
    setActivatedSubscription(null);

    const poll = async () => {
      attempt += 1;
      try {
        const response = await AccountsAPI.get(`/users/${userId}`, { requiresAuth: true });
        if (cancelled) return;
        const user = getApiResponseData(response);
        const subscription = getSubscriptionState({ ...user?.subscription, status: user?.subscription?.type });
        if (subscription.isSubscribed && matchesPurchasedPlan(user?.subscription, purchasedType, purchasedInterval)) {
          setActivatedSubscription(user.subscription);
          await refreshAuthSession();
          if (!cancelled) setActivationState("complete");
          return;
        }
      } catch (error) {
        // The signed-in account, not the return URL, confirms activation.
      }
      if (cancelled) return;
      if (attempt >= 12) setActivationState("delayed");
      else timer = window.setTimeout(poll, 2500);
    };
    void poll();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [checkoutStatus, purchasedType, purchasedInterval, refreshAuthSession, userId]);

  const handleUpgrade = async (plan, product) => {
    if (hasPaidPlan || redirectingPlan || product?.available !== true || checkoutStatus === "success") return;
    setCheckoutError("");
    setRedirectingPlan(`${plan.purchaseType}:${plan.billingInterval}`);
    try {
      const response = await AccountsAPI.post("/users/upgrade", {
        purchaseType: plan.purchaseType,
        billingInterval: plan.billingInterval,
      }, { requiresAuth: true });
      const payload = getApiResponseData(response);
      if (!payload?.url) throw new Error(translate("Checkout could not be opened. Please try again."));
      window.location.assign(payload.url);
    } catch (error) {
      setCheckoutError(error?.response?.data?.message || translate("Unable to start checkout right now. Please try again."));
      setRedirectingPlan(null);
      if (error?.response?.status === 409) await refreshAuthSession();
    }
  };

  return (
    <main className="upgradePage">
      <header className="upgradePage__hero">
        <div className="upgradePage__topline" aria-label={translate("Current account")}>
          <Typography component="span" variant="caption" className="upgradePage__status">
            {currentSubscription.isTrial && <span className="upgradePage__statusDot" aria-hidden="true" />}
            {currentSubscription.isTrial
              ? translate("Your educator trial is active")
              : getAccountPlanName({ ...auth, subscriptionStatus: currentSubscription.status })}
          </Typography>
          {currentSubscription.hasActiveSubscription && auth.subscriptionEndsAt && (
            <Typography component="span" variant="caption" className="upgradePage__statusDate">
              {currentSubscription.renews ? translate("Renews on ") : translate("Access through ")}{formatSubscriptionDate(auth.subscriptionEndsAt)}
            </Typography>
          )}
        </div>
        <Typography component="h1" variant="h2">{hasPaidPlan ? translate("Your subscription") : translate("Choose your plan")}</Typography>
        <Typography variant="body1">
          {hasPaidPlan
            ? translate("Your plan is already active. Manage renewal and view billing history from your account.")
            : translate("Explore on your own, teach your classes, or bring science to your whole site.")}
        </Typography>
        <div className="upgradePage__alerts">
          {checkoutStatus === "cancelled" && <Alert severity="info">{translate("Checkout was cancelled. Your account is unchanged.")}</Alert>}
          {checkoutStatus === "success" && activationState === "pending" && <Alert severity="info">{translate("We're checking your purchase and refreshing your access.")}</Alert>}
          {checkoutStatus === "success" && activationState === "complete" && <Alert severity="success">{translate("Your purchased plan is active.")}</Alert>}
          {checkoutStatus === "success" && activationState === "delayed" && <Alert severity="warning">{translate("We haven't confirmed this plan on your account yet. Refresh this page in a few moments. You do not need to purchase again.")}</Alert>}
          {checkoutError && <Alert severity="error">{checkoutError}</Alert>}
        </div>
      </header>

      {hasPaidPlan ? (
        <section className="upgradePage__manageCard">
          <Typography component="h2" variant="h3">{translate("Manage your current plan")}</Typography>
          <Typography variant="body2">{translate("To change plans, contact us for help with your existing subscription.")}</Typography>
          <div className="upgradePage__inlineActions">
            <AppButton component={Link} to="/account" kind="primary">{translate("Manage Billing")}</AppButton>
            <AppButton component={Link} to="/" kind="secondary">{translate("Back to Home")}</AppButton>
          </div>
        </section>
      ) : (
        <>
          <section className="upgradePage__cards" aria-label={translate("Available plans")}>
            {PLAN_COMPARISON.map((comparison) => {
              const isIndividual = comparison.purchaseType === "consumer_content";
              const isClassroom = comparison.purchaseType === "classroom_license";
              const includesVr = comparison.purchaseType === "site_license";
              const interval = isIndividual ? individualInterval : "year";
              const plan = BILLING_PLANS.find((option) => option.purchaseType === comparison.purchaseType && option.billingInterval === interval);
              const key = `${plan.purchaseType}:${plan.billingInterval}`;
              const product = auth.billingProducts?.find((item) => item.purchaseType === plan.purchaseType && item.billingInterval === plan.billingInterval);
              const available = product?.available === true && Number.isFinite(product?.amount) && product.currency === "usd";
              const [price, priceInterval] = formatPlanPrice(available ? product.amount : plan.amount, plan.billingInterval).split(" / ");
              return (
                <article className={`upgradePage__card${isClassroom ? " upgradePage__card--classroom" : ""}`} key={plan.purchaseType} aria-labelledby={`plan-${plan.purchaseType}`}>
                  <div className="upgradePage__intro">
                    <div className="upgradePage__planTitle">
                      <Typography component="h2" variant="h3" id={`plan-${plan.purchaseType}`}>{comparison.name}</Typography>
                      <Typography component="span" variant="caption" className="upgradePage__platform">{includesVr ? translate("Web + VR") : translate("Web")}</Typography>
                    </div>
                    <Typography variant="body2">{comparison.description}</Typography>
                  </div>
                  <div className="upgradePage__pricing">
                    <Typography component="p" variant="h2" className="upgradePage__price" {...(isIndividual ? { id: "individual-price", role: "tabpanel", "aria-labelledby": `individual-${individualInterval}-tab` } : {})}>
                      {price}{" "}<Typography component="span" variant="body2">/ {priceInterval}</Typography>
                    </Typography>
                    <div className="upgradePage__billing">
                      {isIndividual ? (
                        <Tabs value={individualInterval} onChange={(_, value) => setIndividualInterval(value)}
                          aria-label={translate("Individual billing period")} className="upgradePage__billingTabs"
                          TabIndicatorProps={{ className: "upgradePage__billingIndicator" }}>
                          <Tab id="individual-year-tab" aria-controls="individual-price" value="year" className="upgradePage__billingTab"
                            label={<Typography component="span" variant="caption" className="upgradePage__billingTabLabel">{translate("Annual")}</Typography>} disabled={Boolean(redirectingPlan)} />
                          <Tab id="individual-month-tab" aria-controls="individual-price" value="month" className="upgradePage__billingTab"
                            label={<Typography component="span" variant="caption" className="upgradePage__billingTabLabel">{translate("Monthly")}</Typography>} disabled={Boolean(redirectingPlan)} />
                        </Tabs>
                      ) : <Typography component="span" variant="caption">{translate("Billed annually")}</Typography>}
                    </div>
                  </div>
                  <AppButton kind={isClassroom ? "primary" : "secondary"} loading={redirectingPlan === key} loadingLabel={translate("Opening checkout…")}
                    disabled={!available || Boolean(redirectingPlan) || checkoutStatus === "success"}
                    onClick={() => handleUpgrade(plan, product)}>{translateRich("Choose {value0}", {
  value0: plan.name
})}</AppButton>
                  {!available && <Typography variant="body2" className="upgradePage__availability">
                    {auth.isAccountProfileLoading ? translate("Checking availability\u2026") : translate("Online checkout is not available for this plan yet.")}
                  </Typography>}
                  <div className="upgradePage__includes">
                    <Typography component="h3" variant="subtitle2">{comparison.heading}</Typography>
                    <ul aria-label={translate("{value0} features", {
  value0: comparison.name
})}>
                      {comparison.features.map((feature) => (
                        <Typography component="li" variant="body2" key={feature}>
                          <CheckRoundedIcon aria-hidden="true" /><span>{feature}</span>
                        </Typography>
                      ))}
                    </ul>
                  </div>
                </article>
              );
            })}
          </section>
          <Typography component="p" variant="caption" className="upgradePage__renewal">{translate("Plans renew automatically. Manage renewal anytime from your account.")}</Typography>
          <section className="upgradePage__details" aria-label={translate("Trial and billing details")}>
            <details>
              <Typography component="summary" variant="subtitle2">{translate("What\u2019s included in my 7-day trial?")}</Typography>
              <Typography variant="body2">{translate("Try web content, educator analytics, and student join codes for web and VR. VR setup is included. Custom guide creation is available with a paid Classroom plan or Site license.")}</Typography>
            </details>
            <details>
              <Typography component="summary" variant="subtitle2">{translate("What happens after my trial?")}</Typography>
              <Typography variant="body2">{translate("You can still sign in to your account. Choose a plan to continue accessing content and the tools included with that plan.")}</Typography>
            </details>
            <details>
              <Typography component="summary" variant="subtitle2">{translate("How do I manage renewal?")}</Typography>
              <Typography variant="body2">{translate("Turn off auto-renew from your account at any time. Your access continues through the end of your paid term.")}</Typography>
            </details>
          </section>
        </>
      )}
      {activationState === "pending" && <div className="upgradePage__loadingState"><CircularProgress size={20} /><span>{translate("Refreshing your account\u2026")}</span></div>}
      {!hasPaidPlan && <footer className="upgradePage__footer"><AppButton component={Link} to="/account" kind="ghost">{translate("Back to account")}</AppButton></footer>}
    </main>
  );
};

export default UpgradePage;
